import React, { useState } from 'react'
import styles from './MicrositeSortableNavList.module.css'
import { GripVertical, Trash2 } from 'lucide-react'
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch'

/**
 * MicrositeSortableNavList
 * Drag-to-reorder list of nav links with inline label/path edit
 * and visibility toggle. Used by MicrositeNavigation for both
 * TOP and BOTTOM nav types.
 *
 * Usage:
 *   <MicrositeSortableNavList
 *     links={form.topLinks}
 *     onChange={(next) => updateField('topLinks', next)}
 *   />
 */
const MicrositeSortableNavList = ({ links = [], onChange }) => {
    const [dragIdx, setDragIdx] = useState(null)

    const updateLink = (idx, field, value) => {
        const next = links.map((l, i) => (i === idx ? { ...l, [field]: value } : l))
        onChange?.(next)
    }

    const removeLink = (idx) => {
        onChange?.(links.filter((_, i) => i !== idx))
    }

    const handleDrop = (idx) => {
        if (dragIdx === null || dragIdx === idx) {
            setDragIdx(null)
            return
        }
        const next = [...links]
        const [moved] = next.splice(dragIdx, 1)
        next.splice(idx, 0, moved)
        onChange?.(next.map((l, i) => ({ ...l, sortOrder: i })))
        setDragIdx(null)
    }

    return (
        <div className={styles.list}>
            {links.map((link, idx) => (
                <div
                    key={idx}
                    className={`${styles.row} ${dragIdx === idx ? styles.rowDragging : ''}`}
                    draggable
                    onDragStart={() => setDragIdx(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(idx)}
                >
                    <GripVertical size={14} className={styles.dragHandle} />

                    <input
                        type="text"
                        className={styles.labelInput}
                        value={link.label}
                        onChange={(e) => updateLink(idx, 'label', e.target.value)}
                        placeholder="Label"
                    />
                    <input
                        type="text"
                        className={styles.pathInput}
                        value={link.path}
                        onChange={(e) => updateLink(idx, 'path', e.target.value)}
                        placeholder="/path"
                    />

                    <ToggleSwitch
                        size="sm"
                        checked={!!link.isVisible}
                        onChange={(v) => updateLink(idx, 'isVisible', v)}
                    />

                    <button type="button" className={styles.removeBtn} onClick={() => removeLink(idx)}>
                        <Trash2 size={13} />
                    </button>
                </div>
            ))}
        </div>
    )
}

export default MicrositeSortableNavList